import { useState, type ReactNode } from 'react';
import { useCreditBalance } from '~/hooks/useCreditBalance';

interface CreditRingProps {
  /** The avatar (or any trigger). Scaled to `avatarSize` and centered inside the `outerSize` box. */
  children: ReactNode;
  /** Total box diameter in px — the wrapper AND the ring are this size. Default 32. */
  outerSize?: number;
  /** Visible diameter the avatar is scaled to, centered inside the box. Default 24. */
  avatarSize?: number;
  /** Intrinsic px size of the avatar's OUTER box before scaling. <Avatar />'s box is 36px (a 32px image
   *  inside a 2px transparent border), so map 36 → avatarSize to avoid squeezing the bordered box. */
  childIntrinsicSize?: number;
  /** Stroke width of the ring in px. */
  strokeWidth?: number;
}

/**
 * A circular progress ring showing how much of the user's granted credits have been USED. The arc fills
 * clockwise from 12 o'clock proportional to spend — so the blue grows as credits are used and is empty
 * on a fresh cycle, full when the allotment is exhausted (Cursor-style usage meter). Reads
 * `useCreditBalance` (remaining `credits` + `grantedCredits` total, both real values from the API).
 *
 * Layout: a fixed `outerSize`×`outerSize` box (default 32). The ring fills that box; the avatar is
 * scaled to `avatarSize` (default 24) and centered inside it. Track = gray, progress = brand blue.
 */
export function CreditRing({
  children,
  outerSize = 32,
  avatarSize = 28,
  childIntrinsicSize = 36,
  strokeWidth = 2,
}: CreditRingProps) {
  const { credits, grantedCredits, topupCredits } = useCreditBalance();
  const [hovered, setHovered] = useState(false);

  // Scale the avatar from its intrinsic size (32px image) down to `avatarSize`.
  const childScale = avatarSize / childIntrinsicSize;
  // The ring fills the full box; its track circle is inset by half the stroke so it doesn't clip.
  const center = outerSize / 2;
  const radius = (outerSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Used fraction (0..1) of the MONTHLY allocation. `credits` is the total remaining (monthly + top-up),
  // so monthly remaining = credits − topup, and used = (granted − monthlyRemaining) / granted. The blue
  // arc GROWS from empty toward full as the monthly allocation is spent. (Top-up credits live above the
  // allocation and aren't reflected in the ring; the dropdown shows them as "+N".)
  const hasData = credits != null && grantedCredits != null && grantedCredits > 0;
  const monthlyRemaining = Math.max((credits ?? 0) - topupCredits, 0);
  const usedFraction = hasData
    ? Math.min(Math.max(grantedCredits - monthlyRemaining, 0), grantedCredits) / grantedCredits
    : 0;
  // The visible arc length (the "filled" portion = used credits).
  const dash = circumference * usedFraction;

  // On hover, swap the avatar for the used-credits percentage (matching the arc). Only when we have real
  // data — otherwise hover would flash a misleading "0%" while the balance is still loading.
  const usedPercent = Math.round(usedFraction * 100);
  const showPercent = hovered && hasData;

  return (
    // Fixed outerSize×outerSize box. `verticalAlign: middle` + `lineHeight: 0` drop the inline line-box
    // descender padding so the box stays square in the surrounding inline/flex context.
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: outerSize,
        height: outerSize,
        verticalAlign: 'middle',
        lineHeight: 0,
      }}
    >
      {/* Avatar — a fixed square box at the intrinsic OUTER size, shrunk to `avatarSize` purely via the
          scale transform. `flexShrink: 0` stops the 32px flex parent from squeezing the 36px box (which
          would distort the image); the transform doesn't affect layout, it just scales the visual. */}
      <div
        style={{
          flex: 'none',
          width: childIntrinsicSize,
          height: childIntrinsicSize,
          transform: `scale(${childScale})`,
          transformOrigin: 'center',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 0,
          opacity: showPercent ? 0 : 1,
          transition: 'opacity 150ms ease',
        }}
      >
        {children}
      </div>
      {/* Used-credits percentage — fades in on hover as the avatar fades out, centered in the ring. */}
      <span
        aria-hidden={!showPercent}
        style={{
          position: 'absolute',
          inset: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: Math.round(outerSize * 0.3),
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          color: 'var(--color-text-default)',
          opacity: showPercent ? 1 : 0,
          transition: 'opacity 150ms ease',
          pointerEvents: 'none',
        }}
      >
        {usedPercent}%
      </span>
      <svg
        width={outerSize}
        height={outerSize}
        viewBox={`0 0 ${outerSize} ${outerSize}`}
        fill="none"
        aria-hidden
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        {/* Track — transparent black at 12%, the full ring (unspent baseline). */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="rgba(0, 0, 0, 0.12)"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc — brand blue, the used portion. Starts at 12 o'clock (rotate -90°) and
            sweeps clockwise. Rounded cap reads as the dot at the start. */}
        {dash > 0 ? (
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="var(--color-state-primary)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference}`}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: 'stroke-dasharray 400ms ease' }}
          />
        ) : null}
      </svg>
    </div>
  );
}
